import React, { useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid, Box, Typography } from '@material-ui/core'
import { Context } from '../../../Store'
import { BookingContext } from '../../../utils/BookingContext'
import { BOOKING_FLEXIBILITY_STATUS, BOOKING_PRE_PAYMENT, getCurrency, MAXIMUM_APEPAGO_LOAN_AMOUNT } from '../../../constants'
import { getPricing } from '../../../utils/getPricing'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { formatToItalianCurrency } from '../../../utils/formatToItalianCurrency'
import { isMobile } from 'react-device-detect'

const PriceSummaryHighlight = ({ number, forStepThree }) => {
  return (
    <Box
      style={{
        width: forStepThree ? 32 : 24,
        height: forStepThree ? 32 : 24,
        borderRadius: '50%',
        borderWidth: '1px',
        borderColor: '#0C9D9D',
        borderWidth: 1,
        backgroundColor: number === 1 ? '#0C9D9D' : '#fff',
        color: number === 1 ? '#fff' : '#0C9D9D',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {number}
    </Box>
  )
}

const PriceSummaryRow = ({ title, subtitle, value, currency, highlightNumber, forStepThree, upfrontAndDepositAmount }) => {
  return (
    <Grid container alignItems="flex-start" wrap="nowrap">
      {/* Fixed highlight circle */}
      <Grid item style={{ flexShrink: 0, marginRight: 8 }}>
        <PriceSummaryHighlight number={highlightNumber} forStepThree={forStepThree} />
      </Grid>

      <Grid item xs>
        <Typography
          style={{
            fontWeight: 700,
            fontSize: forStepThree && !isMobile ? '16px' : '14px',
            color: '#000000',
            margin: 0,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            style={{
              fontWeight: 400,
              fontSize: forStepThree && !isMobile ? '14px' : '12px',
              color: '#757575',
              margin: 0,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Grid>

      <Grid item style={{ display: 'flex', alignItems: 'center', flexShrink: 0, textAlign: 'right', marginLeft: 8 }}>
        {upfrontAndDepositAmount && (
          <Typography
            style={{
              fontWeight: 700,
              fontSize: '12px',
              color: '#0000004D',
              margin: 0,
              whiteSpace: 'nowrap',
              opacity: '30%',
              textDecoration: 'line-through',
              marginRight: 4,
            }}
          >
            {upfrontAndDepositAmount} {currency}
          </Typography>
        )}
        <Typography
          style={{
            fontWeight: 700,
            fontSize: '14px',
            color: '#0C9D9D',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {value} {currency}
        </Typography>
      </Grid>
    </Grid>
  )
}

const PriceSummaryTitle = ({ title, forStepThree }) => {
  const [state] = useContext(Context)
  const { t } = useTranslation()
  return (
    <Grid item xs={12} style={{ padding: 0, marginBottom: forStepThree && !isMobile ? 16 : 12 }}>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent:
            state.flexibility_status === BOOKING_FLEXIBILITY_STATUS.READY
              ? 'space-between'
              : forStepThree
              ? 'flex-start'
              : 'center',
        }}
      >
        <Typography
          style={{ fontWeight: 700, fontSize: forStepThree && !isMobile ? '20px' : '16px', color: '#000000', margin: 0 }}
        >
          {title}
        </Typography>
        {state.flexibility_status && (
          <Grid style={{ padding: '7px 8px', borderRadius: '3px', backgroundColor: '#DCF2F4' }}>
            <Typography style={{ fontWeight: 700, marginBottom: 0, fontSize: isMobile ? '11px' : '14px', color: '#0C9D9D' }}>
              {t('easyPayPlan.title')}
            </Typography>
          </Grid>
        )}
      </Box>
    </Grid>
  )
}

export const PricingDetailCard = ({ forStepThree = false }) => {
  const [state] = useContext(Context)
  const { hasCounteroffer } = useContext(BookingContext)
  const { t } = useTranslation()
  const { currency = 'EUR' } = state.property

  const hasPrePayment = state.booking.has_pre_payment
  const { monthly, monthlyMinusPrePayment, upfrontFee, deposit } = useMemo(
    () => getPricing(state.property, state.booking, hasCounteroffer),
    [state.property, state.booking, hasCounteroffer],
  )
  const { value: propertyCurrency } = getCurrency(currency)
  const MONTHLY_RENT = monthly
  const MONTHLY_RENT_WITHOUT_PREPAYMENT = monthlyMinusPrePayment
  const ZAPPYRENT_COMMISSION = upfrontFee
  const DEPOSIT_AMOUNT = deposit
  const COMISSION_PLUS_DEPOSIT = ZAPPYRENT_COMMISSION + DEPOSIT_AMOUNT

  const amountToPayAfterEasyPayPlan = formatToItalianCurrency(Math.max(0, COMISSION_PLUS_DEPOSIT - MAXIMUM_APEPAGO_LOAN_AMOUNT))

  return (
    <section id="paymentSummary">
      <Grid container xs={12} lg={forStepThree ? 11 : 12}>
        <PriceSummaryTitle title={t('propertyDetail.paymentSummary.title')} forStepThree={forStepThree} />
        <Grid
          container
          style={{
            width: '100%',
            backgroundColor: forStepThree ? '#FAFAFA' : isMobile ? '#FBFBFB' : 'transparent',
            borderRadius: '0.625rem',
            border: isMobile ? '0.0625rem solid rgba(229, 229, 229, 0.50)' : 'none',
            padding: forStepThree && !isMobile ? '20px 16px' : isMobile ? '12px 10px' : 0,
            gap: isMobile ? '20px' : '24px',
          }}
        >
          <PriceSummaryRow
            title={t('propertyDetail.paymentSummary.howMuchWillYouPayNow')}
            subtitle={t('propertyDetail.paymentSummary.firstMonthRentDeposit')}
            value={hasPrePayment ? formatToItalianCurrency(BOOKING_PRE_PAYMENT) : formatToItalianCurrency(0)}
            currency={propertyCurrency}
            highlightNumber={1}
            forStepThree={forStepThree}
          />

          <PriceSummaryRow
            title={t('propertyDetail.paymentSummary.afterLandlordAccept')}
            subtitle={
              hasPrePayment
                ? t('propertyDetail.paymentSummary.firstMonthRentBalanceWithPrePayment', {
                    amount: formatToItalianCurrency(MONTHLY_RENT),
                    pre_payment: formatToItalianCurrency(BOOKING_PRE_PAYMENT),
                  })
                : t('propertyDetail.paymentSummary.firstMonthRentBalanceWithoutPrePayment', {
                    amount: formatToItalianCurrency(MONTHLY_RENT),
                  })
            }
            value={
              hasPrePayment ? formatToItalianCurrency(MONTHLY_RENT_WITHOUT_PREPAYMENT) : formatToItalianCurrency(MONTHLY_RENT)
            }
            currency={propertyCurrency}
            highlightNumber={2}
            forStepThree={forStepThree}
          />

          <PriceSummaryRow
            title={t('propertyDetail.paymentSummary.approvedCreditPaymentBeforeEnteringHome')}
            subtitle={t('propertyDetail.paymentSummary.zappyrentCommissionAndDepsoit', {
              commission: formatToItalianCurrency(ZAPPYRENT_COMMISSION),
              deposit: formatToItalianCurrency(DEPOSIT_AMOUNT),
            })}
            value={state.flexibility_status === BOOKING_FLEXIBILITY_STATUS.READY ? amountToPayAfterEasyPayPlan : formatToItalianCurrency(COMISSION_PLUS_DEPOSIT)}
            currency={propertyCurrency}
            highlightNumber={3}
            forStepThree={forStepThree}
            upfrontAndDepositAmount={state.flexibility_status === BOOKING_FLEXIBILITY_STATUS.READY && formatToItalianCurrency(COMISSION_PLUS_DEPOSIT)}
          />
        </Grid>
      </Grid>
    </section>
  )
}

PricingDetailCard.propTypes = {
  forStepThree: PropTypes.bool,
}

export default PricingDetailCard
